import type {
  HelpdeskTicketScope,
  IHelpdeskLimits,
  IHelpdeskTicketContext,
  IHelpdeskTicketDetail,
  IHelpdeskTicketPage,
  IHelpdeskTicketSummary,
  IHelpdeskTicketUpdatedEvent
} from "@kuika/kuika-cl-model";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { HelpdeskService } from "../../../services/helpdesk-service";
import useLoadingPresentation, { LoadingPresentation } from "../../../shared/hooks/useLoadingPresentation";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs, { KuikaTabOption } from "../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import "./helpdesk.scss";
import { readHelpdeskError } from "./helpdesk-errors";
import { subscribeHelpdeskEvents } from "./helpdesk-events";
import HelpdeskNewTicketForm from "./helpdesk-new-ticket-form";
import HelpdeskTicketList from "./helpdesk-ticket-list";
import HelpdeskTicketSkeleton from "./helpdesk-ticket-skeleton";
import HelpdeskTicketViewer from "./helpdesk-ticket-viewer";
import { mergeTickets } from "./helpdesk-tickets";

export interface ItsmHelpdeskProps {
  userId: string | undefined;
  workspaceName: string | undefined;
  limits: IHelpdeskLimits;
  getTicketContext: () => IHelpdeskTicketContext;
  /** Open on the new-ticket form instead of the newest ticket. */
  startWithNewTicket?: boolean;
  /** Open on this ticket (the "View ticket" link of an ITSM mail) instead of the newest one. */
  initialTicketNumber?: number | null;
  /** The unread count may have changed (a ticket was read or updated): refresh the badge. */
  onUnreadChanged: () => void;
}

/** Opening on the newest ticket: which one that is is known once the list arrives. */
interface IPendingView {
  kind: "pending";
}

interface IEmptyView {
  kind: "empty";
}

interface INewTicketView {
  kind: "new";
}

interface ITicketView {
  kind: "ticket";
  ticketNumber: number;
  /** Opened from the pending view while its skeleton was on screen: the viewer goes on showing one. */
  continuesSkeleton?: boolean;
}

type HelpdeskView = IPendingView | IEmptyView | INewTicketView | ITicketView;

// The ITSM caps a page at 100.
const PAGE_SIZE: number = 100;

const SCOPE_OPTIONS: KuikaTabOption[] = [
  { value: "mine", label: "Mine", dataTestId: "helpdeskScopeMine" },
  { value: "company", label: "Workspace", dataTestId: "helpdeskScopeWorkspace" }
];

const NOT_FOUND_MESSAGE: string = "This ticket couldn't be found, or you don't have access to it.";

/** The newest change among `tickets`: the next refresh asks only for what changed after it. */
function latestUpdate(tickets: readonly IHelpdeskTicketSummary[]): string | null {
  let latest: string | null = null;
  let latestTime: number = Number.NEGATIVE_INFINITY;
  for (const ticket of tickets) {
    const time: number = Date.parse(ticket.updatedAt);
    if (Number.isFinite(time) && time > latestTime) {
      latestTime = time;
      latest = ticket.updatedAt;
    }
  }
  return latest;
}

function initialView(startWithNewTicket: boolean, initialTicketNumber: number | null | undefined): HelpdeskView {
  if (initialTicketNumber) return { kind: "ticket", ticketNumber: initialTicketNumber };
  if (startWithNewTicket) return { kind: "new" };
  return { kind: "pending" };
}

/** The designer helpdesk on the Kuika ITSM: ticket list on the left, the form or a ticket on the right. */
const ItsmHelpdesk = ({
  userId,
  workspaceName,
  limits,
  getTicketContext,
  startWithNewTicket = false,
  initialTicketNumber = null,
  onUnreadChanged
}: ItsmHelpdeskProps): React.ReactElement => {
  const [tickets, setTickets] = useState<IHelpdeskTicketSummary[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loadedPages, setLoadedPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [listError, setListError] = useState<string | null>(null);
  const [scope, setScope] = useState<HelpdeskTicketScope>("mine");
  // Whether the person's company shares its tickets (contract §3.2, plan E109): a per-company ITSM
  // setting, off by default, never on for an individual. The Workspace tab exists only while it is.
  // Every current list answer carries it; a failed request keeps the last value.
  const [companyTicketsVisible, setCompanyTicketsVisible] = useState<boolean>(false);
  const [view, setView] = useState<HelpdeskView>((): HelpdeskView =>
    initialView(startWithNewTicket, initialTicketNumber)
  );
  const [searchText, setSearchText] = useState<string>("");
  // Bumped when the stream reports a change to the open ticket, so the viewer re-reads it.
  const [ticketRefreshKey, setTicketRefreshKey] = useState<number>(0);
  const firstLoad: React.MutableRefObject<boolean> = useRef<boolean>(true);
  const ticketsRef: React.MutableRefObject<IHelpdeskTicketSummary[]> = useRef<IHelpdeskTicketSummary[]>([]);
  ticketsRef.current = tickets;
  const viewRef: React.MutableRefObject<HelpdeskView> = useRef<HelpdeskView>(view);
  viewRef.current = view;
  const onUnreadChangedRef: React.MutableRefObject<() => void> = useRef<() => void>(onUnreadChanged);
  onUnreadChangedRef.current = onUnreadChanged;
  // Both regions wait for the first list request: the list for its rows, the main panel for the
  // ticket to open on.
  const listPresentation: LoadingPresentation = useLoadingPresentation({
    isLoading: loading,
    hasData: tickets.length > 0
  });
  const mainPresentation: LoadingPresentation = useLoadingPresentation({
    isLoading: view.kind === "pending",
    hasData: false
  });
  // Read when the list answers, which is always after a commit, so the effect keeps it current.
  const mainPresentationRef: React.MutableRefObject<LoadingPresentation> =
    useRef<LoadingPresentation>(mainPresentation);
  useEffect((): void => {
    mainPresentationRef.current = mainPresentation;
  }, [mainPresentation]);

  // Bumped by every full load. A response that comes back after a newer load started (the user
  // switched Mine/Workspace meanwhile) belongs to the other scope and is dropped.
  const listGenerationRef: React.MutableRefObject<number> = useRef<number>(0);

  /**
   * Moves the list to another scope. The tickets on screen belong to the old one, so they are dropped
   * and the switch shows the loading skeleton instead of the old list under the new tab; an answer
   * still in flight for the old scope is superseded. The new scope's load starts from the effect.
   */
  const switchScope = useCallback((next: HelpdeskTicketScope): void => {
    listGenerationRef.current += 1;
    setTickets([]);
    setTotalCount(0);
    setLoadedPages(0);
    setLoading(true);
    setScope(next);
  }, []);

  /**
   * Takes a current list answer's `companyTicketsVisible`. If the company stopped sharing while the
   * person is on Workspace, that list is only their own now: back to Mine, reloaded. Returns true
   * when it moved the list, so the caller drops the answer.
   */
  const acceptCompanyVisibility = useCallback(
    (visible: boolean): boolean => {
      setCompanyTicketsVisible(visible);
      if (visible || scope !== "company") return false;
      switchScope("mine");
      return true;
    },
    [scope, switchScope]
  );

  /** The first page of the scope, from scratch; null when a newer load superseded this one. */
  const loadTickets = useCallback(async (): Promise<IHelpdeskTicketSummary[] | null> => {
    listGenerationRef.current += 1;
    const generation: number = listGenerationRef.current;
    setListError(null);
    try {
      const page: IHelpdeskTicketPage = await HelpdeskService.getTickets(scope, "all", 1, PAGE_SIZE);
      if (generation !== listGenerationRef.current) return null;
      if (acceptCompanyVisibility(page?.companyTicketsVisible === true)) return null;
      const items: IHelpdeskTicketSummary[] = page?.items ?? [];
      setTickets(items);
      setTotalCount(page?.totalCount ?? items.length);
      setLoadedPages(1);
      return items;
    } catch (error) {
      if (generation !== listGenerationRef.current) return null;
      setListError(readHelpdeskError(error).message);
      return [];
    } finally {
      if (generation === listGenerationRef.current) setLoading(false);
    }
  }, [scope, acceptCompanyVisibility]);

  /**
   * Only what changed since the newest ticket on screen (`updatedSince`), folded into the list; a
   * full reload when nothing is loaded yet or more changed than one page holds.
   */
  const refreshTickets = useCallback(async (): Promise<void> => {
    const since: string | null = latestUpdate(ticketsRef.current);
    if (!since) {
      await loadTickets();
      return;
    }
    const generation: number = listGenerationRef.current;
    try {
      const page: IHelpdeskTicketPage = await HelpdeskService.getTickets(scope, "all", 1, PAGE_SIZE, since);
      if (generation !== listGenerationRef.current) return;
      if (acceptCompanyVisibility(page?.companyTicketsVisible === true)) return;
      const changed: IHelpdeskTicketSummary[] = page?.items ?? [];
      if ((page?.totalCount ?? 0) > changed.length) {
        await loadTickets();
        return;
      }
      const known: Set<number> = new Set<number>(
        ticketsRef.current.map((ticket: IHelpdeskTicketSummary): number => ticket.ticketNumber)
      );
      const added: number = changed.filter(
        (ticket: IHelpdeskTicketSummary): boolean => !known.has(ticket.ticketNumber)
      ).length;
      setTickets((current: IHelpdeskTicketSummary[]): IHelpdeskTicketSummary[] => mergeTickets(current, changed));
      if (added > 0) setTotalCount((current: number): number => current + added);
    } catch {
      // The list on screen stays; the next change or reopening refreshes it.
    }
  }, [loadTickets, scope, acceptCompanyVisibility]);

  /** The next page, merged into the list; returns its tickets (none on failure). */
  const loadMore = async (): Promise<IHelpdeskTicketSummary[]> => {
    if (loadingMore) return [];
    setLoadingMore(true);
    const generation: number = listGenerationRef.current;
    try {
      const page: IHelpdeskTicketPage = await HelpdeskService.getTickets(scope, "all", loadedPages + 1, PAGE_SIZE);
      if (generation !== listGenerationRef.current) return [];
      if (acceptCompanyVisibility(page?.companyTicketsVisible === true)) return [];
      const items: IHelpdeskTicketSummary[] = page?.items ?? [];
      setTickets((current: IHelpdeskTicketSummary[]): IHelpdeskTicketSummary[] => mergeTickets(current, items));
      setTotalCount(page?.totalCount ?? totalCount);
      setLoadedPages((current: number): number => current + 1);
      return items;
    } catch (error) {
      KMainFunctions.displayErrorNotification(readHelpdeskError(error).message);
      return [];
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect((): void => {
    setLoading(true);
    loadTickets().then((items: IHelpdeskTicketSummary[] | null): void => {
      // Superseded by a scope switch: the newer load decides the first view.
      if (items === null || !firstLoad.current) return;
      firstLoad.current = false;
      const newest: IHelpdeskTicketSummary | undefined = [...items].sort(
        (a: IHelpdeskTicketSummary, b: IHelpdeskTicketSummary): number =>
          Date.parse(b.updatedAt) - Date.parse(a.updatedAt)
      )[0];
      const continuesSkeleton: boolean = mainPresentationRef.current === "skeleton";
      // Only the pending view is decided here. A page opened on a ticket or the form, or a choice the
      // user made while the list was loading, stays as it is.
      setView((current: HelpdeskView): HelpdeskView => {
        if (current.kind !== "pending") return current;
        return newest ? { kind: "ticket", ticketNumber: newest.ticketNumber, continuesSkeleton } : { kind: "new" };
      });
    });
  }, [loadTickets, startWithNewTicket, initialTicketNumber]);

  // Support replied, changed the status or removed a message: refresh the list, and the open
  // ticket when it is the one that changed. No polling; this is pushed by CoreApi.
  useEffect(
    (): (() => void) =>
      subscribeHelpdeskEvents((event: IHelpdeskTicketUpdatedEvent): void => {
        refreshTickets();
        const current: HelpdeskView = viewRef.current;
        if (current.kind === "ticket" && current.ticketNumber === event.ticketNumber) {
          setTicketRefreshKey((key: number): number => key + 1);
        }
      }),
    [refreshTickets]
  );

  const onTicketChanged = useCallback((): void => {
    refreshTickets();
    onUnreadChangedRef.current();
  }, [refreshTickets]);

  const onTicketNotFound = useCallback((): void => {
    KMainFunctions.displayWarningNotification(NOT_FOUND_MESSAGE);
    setView({ kind: "empty" });
  }, []);

  const changeScope = (value: string): void => {
    const next: HelpdeskTicketScope = value === "company" ? "company" : "mine";
    if (next !== scope) switchScope(next);
  };

  const onCreated = (ticket: IHelpdeskTicketDetail): void => {
    setView({ kind: "ticket", ticketNumber: ticket.ticketNumber });
    refreshTickets();
  };

  const selectedTicketNumber: number | null = view.kind === "ticket" ? view.ticketNumber : null;

  const renderList = (): React.ReactElement | null => {
    if (listPresentation === "skeleton") {
      return (
        <KuikaSkeletonRegion label="Loading tickets" className="kuika-helpdesk-list__skeleton">
          <KuikaSkeletonList rows={6} lines={2} withIcon={false} density="comfortable" />
        </KuikaSkeletonRegion>
      );
    }
    // A fast load lands before the skeleton is due: hold the space instead of flashing "no tickets".
    if (listPresentation === "pending") return null;

    if (listError) {
      return (
        <div className="kuika-helpdesk-list__empty">
          <KuikaSmallText>{listError}</KuikaSmallText>
        </div>
      );
    }

    return (
      <HelpdeskTicketList
        tickets={tickets}
        searchText={searchText}
        selectedTicketNumber={selectedTicketNumber}
        onSelect={(ticketNumber: number): void => setView({ kind: "ticket", ticketNumber })}
        remainingCount={Math.max(totalCount - tickets.length, 0)}
        loadingMore={loadingMore}
        onLoadMore={loadMore}
      />
    );
  };

  const renderMain = (): React.ReactElement | null => {
    if (view.kind === "pending") {
      // The list beside it already announces the wait.
      return mainPresentation === "skeleton" ? <HelpdeskTicketSkeleton announce={false} /> : null;
    }

    if (view.kind === "new") {
      return (
        <HelpdeskNewTicketForm
          userId={userId}
          workspaceName={workspaceName}
          limits={limits}
          getTicketContext={getTicketContext}
          onCreated={onCreated}
          onCancel={(): void => setView({ kind: "empty" })}
        />
      );
    }

    if (view.kind === "ticket") {
      return (
        <HelpdeskTicketViewer
          key={view.ticketNumber}
          ticketNumber={view.ticketNumber}
          limits={limits}
          onTicketChanged={onTicketChanged}
          refreshKey={ticketRefreshKey}
          onNotFound={onTicketNotFound}
          skeletonDelayMs={view.continuesSkeleton ? 0 : undefined}
        />
      );
    }

    return (
      <div className="kuika-helpdesk__placeholder">
        <KuikaSvg name="help-square" type="sidebar-icons" width={32} height={32} color="var(--dark-5)" />
        <KuikaSmallText>Select a ticket or open a new one.</KuikaSmallText>
      </div>
    );
  };

  return (
    <div className="kuika-helpdesk" data-testid="itsmHelpdesk">
      <aside className="kuika-helpdesk__sidebar">
        <div className="kuika-helpdesk__sidebar-header">
          <KuikaTextMedium>Tickets</KuikaTextMedium>
          <KuikaButton
            data-testid="newTicketButton"
            aria-label="New ticket"
            type="tertiary"
            color="neutral"
            isIcon
            disabled={view.kind === "new"}
            onClick={(): void => setView({ kind: "new" })}
          >
            <KuikaSvg name="plus" width={16} height={16} />
          </KuikaButton>
        </div>
        <div className="kuika-helpdesk__sidebar-tools">
          <KuikaInput
            size="sm"
            aria-label="Search tickets"
            placeholder="Search..."
            leftIcon={<KuikaSvg name="search" width={14} height={14} />}
            value={searchText}
            onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
              setSearchText(String(value))
            }
          />
          {/* Without a shared company, Workspace would only repeat Mine: no tabs, the list is Mine. */}
          {companyTicketsVisible && (
            <KuikaTabs
              dataTestId="helpdeskScopeTabs"
              ariaLabel="Whose tickets"
              value={scope}
              options={SCOPE_OPTIONS}
              onChange={changeScope}
            />
          )}
        </div>
        <div className="kuika-helpdesk__sidebar-list">{renderList()}</div>
      </aside>
      <main className="kuika-helpdesk__main">{renderMain()}</main>
    </div>
  );
};

export default ItsmHelpdesk;
